/**
 * Typography constants for consistent styling across components.
 *
 * These use Tailwind CSS utility classes directly since custom CSS classes
 * with @apply don't work reliably in Tailwind CSS v4.
 *
 * Usage:
 * ```tsx
 * import { typography } from '@/styles/typography';
 *
 * <span className={cn(typography.subtitle, 'text-foreground')}>
 *   Title
 * </span>
 * ```
 */
export const typography = {
  /**
   * Heading small typography
   * - Font: Source Sans Pro
   * - Size: 24px
   * - Weight: 600 (semibold)
   * - Line height: 32px
   */
  headingSmall: "font-semibold text-[24px] leading-[32px] font-['Source_Sans_Pro']",

  /**
   * Subtitle typography
   * - Font: Source Sans Pro
   * - Size: 20px
   * - Weight: 600 (semibold)
   * - Line height: 28px
   */
  subtitle: "font-semibold text-[20px] leading-[28px] font-['Source_Sans_Pro']",

  /**
   * Body typography
   * - Font: Source Sans Pro
   * - Size: 16px
   * - Weight: 400 (normal)
   * - Line height: 24px
   */
  body: "font-normal text-[16px] leading-[24px] font-['Source_Sans_Pro']",

  /**
   * Button default typography
   * - Font: Source Sans Pro
   * - Size: 16px
   * - Weight: 600 (semibold)
   * - Line height: 24px
   */
  buttonDefault: "font-semibold text-[16px] leading-[24px] font-['Source_Sans_Pro']",

  /**
   * Button large typography
   * - Font: Source Sans Pro
   * - Size: 20px
   * - Weight: 600 (semibold)
   * - Line height: 28px
   * - Color: black
   */
  buttonLarge: "font-semibold text-[20px] leading-[28px] font-['Source_Sans_Pro'] text-[var(--color-text-black)]",

  /**
   * Heading X Large typography
   * - Font: Source Sans Pro
   * - Size: 40px
   * - Weight: 600 (semibold)
   * - Line height: 56px
   */
  headingXLarge: "font-semibold text-[40px] leading-[56px] font-['Source_Sans_Pro']",

  /**
   * Heading large typography
   * - Font: Source Sans Pro
   * - Size: 24px
   * - Weight: 600 (semibold)
   * - Line height: 32px
   */
  headingLarge: "font-semibold text-[24px] leading-[32px] font-['Source_Sans_Pro']",

  /**
   * Heading medium typography
   * - Font: Source Sans Pro
   * - Size: 28px
   * - Weight: 600 (semibold)
   * - Line height: 36px
   */
  headingMedium: "font-semibold text-[28px] leading-[36px] font-['Source_Sans_Pro']",

  /**
   * Body medium typography
   * - Font: Source Sans Pro
   * - Size: 20px
   * - Weight: 400 (regular)
   * - Line height: 28px
   */
  bodyMedium: "font-normal text-[20px] leading-[28px] font-['Source_Sans_Pro']",

  /**
   * Body large typography
   * - Font: Source Sans Pro
   * - Size: 24px
   * - Weight: 400 (regular)
   * - Line height: 32px
   */
  bodyLarge: "font-normal text-[24px] leading-[32px] font-['Source_Sans_Pro']",

  /**
   * Body large semibold typography (for links in body large text)
   * - Font: Source Sans Pro
   * - Size: 24px
   * - Weight: 600 (semibold)
   * - Line height: 32px
   */
  bodyLargeSemibold: "font-semibold text-[24px] leading-[32px] font-['Source_Sans_Pro']",

  // ============================================================================
  // Enlarged Font Mode Typography (Accessibility)
  // ============================================================================

  /**
   * Enlarged title typography (for increased font mode)
   * - Font: Source Sans Pro
   * - Size: 32px
   * - Weight: 600 (semibold)
   * - Line height: 100%
   */
  enlargedTitle: "font-semibold text-[32px] leading-[100%] font-['Source_Sans_Pro']",

  /**
   * Enlarged body typography (for increased font mode)
   * - Font: Source Sans Pro
   * - Size: 24px
   * - Weight: 400 (regular)
   * - Line height: 100%
   */
  enlargedBody: "font-normal text-[24px] leading-[100%] font-['Source_Sans_Pro']",

  /**
   * Enlarged question typography (for increased font mode feedback)
   * - Font: Source Sans Pro
   * - Size: 28px
   * - Weight: 600 (semibold)
   * - Line height: 100%
   */
  enlargedQuestion: "font-semibold text-[28px] leading-[100%] font-['Source_Sans_Pro']",
} as const;

/** Type for typography keys */
export type TypographyKey = keyof typeof typography;
